import styled, { css } from 'styled-components';
import { TypeTaskStatus, TaskStatusColor } from '../../components/enum/taskStatusEnum';

export {
    Container,
    Title,
    TableContainer,
    ActionButton,
    Table,
    ActionsCell,
    TextFeldClientSearch,
    SearchCard as TaskSearchCard,
} from '../sharedTable.styled';

/* ============================================================
   Quadro kanban — três colunas com drag & drop nativo (HTML5),
   sem dependência extra. Themado pelas mesmas CSS vars das
   demais páginas.
   ============================================================ */

export const Board = styled.div`
  width: 92%;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Column = styled.div<{ $status: TypeTaskStatus; $over: boolean }>`
  position: relative;
  background: var(--surface);
  border: 1px solid
    ${({ $over, $status }) => ($over ? TaskStatusColor[$status] : 'var(--border)')};
  box-shadow: ${({ $over }) => ($over ? 'var(--glow)' : 'var(--shadow)')};
  border-radius: 18px;
  padding: 14px;
  transition: border-color 0.22s ease, box-shadow 0.22s ease;

  /* Lavagem na cor da coluna quando ela é o destino: diz "solta aqui" sem
     precisar de borda tracejada nem texto. Vai num ::before para o fundo
     poder ter opacidade própria sem afetar os cards. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: ${({ $status }) => TaskStatusColor[$status]};
    opacity: ${({ $over }) => ($over ? 0.07 : 0)};
    transition: opacity 0.22s ease;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::before {
      transition: none;
    }
  }
`;

export const ColumnHeader = styled.div<{ $status: TypeTaskStatus }>`
  /* acima da lavagem de destino da coluna */
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 2px solid ${({ $status }) => TaskStatusColor[$status]};

  h2 {
    flex: 1;
    margin: 0;
    font-family: var(--font-display);
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${({ $status }) => TaskStatusColor[$status]};
  }

  span {
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 700;
    color: var(--text-muted);
  }
`;

/* A coluna mostra 6 cards e rola o resto — e o quadro inteiro precisa caber na
   tela, então quem encolhe é o card, não a coluna que cresce.
   Card compacto: título e descrição em UMA linha cada (o texto inteiro fica no
   tooltip) e rodapé ancorado embaixo, então todo card mede CARD_HEIGHT.
   Conta da altura útil (74 - 16 de padding - 2 de borda = 56):
   título 17 + 3 de margem, descrição 15 + 3 de margem, rodapé 18.
   Resultado: 6 cards ocupam 6x74 + 5x8 = 484px de lista. */
const CARD_HEIGHT = 74;
const CARD_GAP = 8;
const VISIBLE_CARDS = 6;

export const CardList = styled.div`
  /* acima da lavagem de destino da coluna */
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${CARD_GAP}px;
  min-height: 80px;
  max-height: ${VISIBLE_CARDS * CARD_HEIGHT + (VISIBLE_CARDS - 1) * CARD_GAP}px;
  overflow-y: auto;
  /* respiro para a barra de rolagem não encostar no card */
  padding-right: 4px;

  scrollbar-width: thin;
  scrollbar-color: var(--scroll-thumb) transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--scroll-thumb);
    border-radius: 999px;
  }
`;

/* Deslocamento que abre o "buraco" onde o card vai cair. É transform, não
   margem: transform não refaz layout, então a lista não treme e o vão abre
   junto com o movimento do cursor. */
const DROP_GAP = 9;

/* A borda é o próprio andamento da tarefa: amarelo (a fazer) -> azul
   (em progresso) -> verde (concluído). Nunca vermelho.

   Coreografia do arrasto:
   - `$dragging`  card de origem vira fantasma no lugar
   - `$shifted`   cards abaixo do destino descem e abrem o vão
   - `$dropHint`  linha de acento na beirada exata onde ele encaixa       */
export const Card = styled.div<{
  $status: TypeTaskStatus;
  $group?: boolean;
  $dropHint?: 'before' | 'after' | null;
  $shifted?: boolean;
  $dragging?: boolean;
  $dragActive?: boolean;
}>`
  box-sizing: border-box;
  height: ${CARD_HEIGHT}px;
  /* sem isso o flex da coluna espreme os cards em vez de rolar */
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--surface-2);
  /* Tarefa de grupo se distingue só pelo contorno tracejado — "aberto ao
     time". A individual fica de contorno fechado. */
  border: 1px ${({ $group }) => ($group ? 'dashed' : 'solid')}
    ${({ $status }) => TaskStatusColor[$status]};
  border-left: 4px solid ${({ $status }) => TaskStatusColor[$status]};
  border-radius: 10px;
  padding: 8px 12px;
  cursor: grab;
  color: var(--text);
  /* a mesma curva de saída em tudo que se move: nada "cai", tudo desacelera */
  transition: transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1),
    box-shadow 0.18s ease, opacity 0.16s ease;
  will-change: transform;

  &:active {
    cursor: grabbing;
  }

  /* O levantar no hover só faz sentido parado. Durante o arrasto ele brigaria
     com o deslocamento do vão e o card daria um pulo. */
  ${({ $dragActive }) =>
        !$dragActive &&
        css`
      &:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-lg);
      }
    `}

  ${({ $shifted }) =>
        $shifted &&
        css`
      transform: translateY(${DROP_GAP}px);
    `}

  ${({ $dropHint }) =>
        $dropHint === 'before' &&
        css`
      box-shadow: inset 0 3px 0 0 var(--accent), 0 0 0 1px var(--accent);
    `}

  ${({ $dropHint }) =>
        $dropHint === 'after' &&
        css`
      box-shadow: inset 0 -3px 0 0 var(--accent), 0 0 0 1px var(--accent);
    `}

  ${({ $dragging }) =>
        $dragging &&
        css`
      opacity: 0.35;
      /* encolhe de leve: lê como "isto saiu do lugar" sem sumir da lista */
      transform: scale(0.97);
      box-shadow: none;
      cursor: grabbing;
    `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  h3 {
    margin: 0;
    flex: 1;
    min-width: 0;
    font-size: 14px;
    line-height: 17px;
    font-weight: 700;
    font-family: var(--font-display);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  p {
    margin: 0 0 3px;
    font-size: 12px;
    line-height: 15px;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/* Linha do topo: número da posição + título. Ocupa a mesma altura que o
   título ocupava sozinho, então a conta do CARD_HEIGHT não muda. */
export const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 17px;
  margin-bottom: 3px;
`;

/** Posição do card na coluna — continua visível ao rolar a lista. */
export const CardIndex = styled.span<{ $status: TypeTaskStatus }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 17px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: ${({ $status }) => TaskStatusColor[$status]};
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
`;

/** Pilha de iniciais dos responsáveis, no canto direito da linha do título. */
export const AssigneeStack = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  /* sobreposição leve: cabe mais gente sem esticar a linha */
  & > * + * {
    margin-left: -6px;
  }
`;

export const AssigneeDot = styled.span<{ $self?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  /* o anel na cor do card separa as bolinhas sobrepostas */
  border: 1.5px solid var(--surface-2);
  background: ${({ $self }) => ($self ? 'var(--accent)' : 'var(--text-dim)')};
  color: #fff;
  font-size: 8px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
`;

/** Marca a tarefa de grupo que ainda não tem ninguém: livre para quem pegar. */
export const FreeTag = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  height: 16px;
  padding: 0 6px;
  border: 1px dashed var(--accent);
  border-radius: 999px;
  color: var(--accent);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 18px;
  /* ancora embaixo: card sem descrição continua com a mesma altura */
  margin-top: auto;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-dim);

  .due {
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 8px;

    /* o ActionButton compartilhado vem com margin-left: 15px, folga demais
       para o card compacto — aqui quem espaça é o gap */
    button {
      margin-left: 0;
      display: inline-flex;
      align-items: center;
    }
  }
`;

export const EmptyColumn = styled.div<{ $active?: boolean }>`
  border: 1px dashed
    ${({ $active }) => ($active ? 'var(--accent)' : 'var(--border-strong)')};
  border-radius: 10px;
  padding: 14px;
  text-align: center;
  font-size: 12px;
  color: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--text-dim)')};
  transition: border-color 0.2s ease, color 0.2s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const Toolbar = styled.div`
  width: 92%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 18px;
`;

export const GhostButton = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: 10px;
  border: 1px solid ${({ $danger }) => ($danger ? 'var(--accent-rose)' : 'var(--border)')};
  background: var(--surface-2);
  color: ${({ $danger }) => ($danger ? 'var(--accent-rose)' : 'var(--text)')};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover:not(:disabled) {
    border-color: ${({ $danger }) => ($danger ? 'var(--accent-rose)' : 'var(--accent)')};
    color: ${({ $danger }) => ($danger ? 'var(--accent-rose)' : 'var(--accent)')};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** Painel central quando ADMIN ainda não escolheu a empresa — evita mostrar
 * as três colunas vazias, que sugeririam "sem tarefa" em vez de "escolha
 * uma empresa". */
export const CompanyPrompt = styled.div`
  width: 92%;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  background: var(--surface);
  border: 1.5px dashed var(--border-strong);
  border-radius: 16px;
  color: var(--text-muted);

  svg {
    font-size: 30px;
    color: var(--text-dim);
  }

  strong {
    font-size: 15px;
    color: var(--text);
  }

  p {
    margin: 0;
    font-size: 13px;
    max-width: 360px;
  }
`;

/** Contador vermelho ao lado de "Não realizadas". */
export const Badge = styled.span`
  background: var(--accent-rose);
  color: #fff;
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 12px;
  font-weight: 700;
`;
